import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import PropTypes from "prop-types";
import CustomAlert from "common/Components/CustomAlert";
import { useNavigate } from "react-router-dom";
import { groupsListStyles, groupsListCSS } from "./GroupsList.style";
import GROUPS_CONSTANTS from "../GroupsConstant";
import { GroupsServices } from "../Groups.services";
import GroupAccessDrawer from "./GroupAccessDrawer";
import { SelectedCustomerAccessProvider } from "./SelectedCustomerAccessContext";
import Toast from "common/Components/Toast";

const GroupsList = ({
  groups,
  handelEditGroups,
  handleEditGroupAction,
  handleSearchGroup,
  getAllGroups,
}) => {
  const { tableHeaders } = INFRAMARK_CONSTANTS.groups;
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertInfo, setAlertInfo] = useState({});
  const [accessDrawerOpen, setAccessDrawerOpen] = useState(false);
  const [selectedGroupForAccess, setSelectedGroupForAccess] = useState(null);
  const [dataConnectionsApplying, setDataConnectionsApplying] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
    title: "",
    description: "",
  });
  const navigate = useNavigate();
  const groupsServices = useMemo(() => GroupsServices(), []);
  const selectedGroupForAccessRef = useRef(selectedGroupForAccess);
  selectedGroupForAccessRef.current = selectedGroupForAccess;
  const dismissToast = useCallback(() => {
    setToast((t) => ({ ...t, show: false }));
  }, []);
  const handleDataConnectionsApply = useCallback(
    async (payload) => {
      // WMP-2395: PATCH /customers/{code} is keyed by the customer code; only
      const customerCode =
        selectedGroupForAccess?.code ??
        selectedGroupForAccess?.customerCode 
      setDataConnectionsApplying(true);
      setToast((t) => ({ ...t, show: false }));
      try {
        const res = await groupsServices.patchCustomerDataConnections(
          customerCode,
          payload
        );
        if (res?.error) {
          const errorMessage =
            res?.error?.trim() || GROUPS_CONSTANTS.toast.MESSAGE_FAILURE;
          setToast({
            show: true,
            type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
            title: GROUPS_CONSTANTS.groups.TOAST_ERROR_TITLE,
            description: errorMessage,
          });
          return { ok: false, error: errorMessage };
        }
        if (res?.status === 200 || res?.status === 204) {
          setToast({
            show: true,
            type: GROUPS_CONSTANTS.toast.TYPE_SUCCESS,
            title: GROUPS_CONSTANTS.toast.TITLE_SUCCESS,
            description:
              GROUPS_CONSTANTS.groupsList.DATA_CONNECTIONS_UPDATE_SUCCESS,
          });
          getAllGroups();
          return { ok: true };
        }
        return { ok: false };
      } catch (err) {
        const errorMessage =
          err?.error?.trim() || GROUPS_CONSTANTS.toast.MESSAGE_FAILURE;
        setToast({
          show: true,
          type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
          title: GROUPS_CONSTANTS.groups.TOAST_ERROR_TITLE,
          description: errorMessage,
        });
        return { ok: false, error: errorMessage };
      } finally {
        setDataConnectionsApplying(false);
      }
    },
    [
      selectedGroupForAccess?.code,
      selectedGroupForAccess?.customerCode,
      groupsServices,
      getAllGroups,
    ]
  );

  useEffect(() => {
    if (!accessDrawerOpen) return;
    const current = selectedGroupForAccessRef.current;
    if (current == null) return;
    const id = current.code;
    const refreshed = groups?.find((g) => String(g?.code) === String(id));
    if (refreshed) {
      setSelectedGroupForAccess((prev) =>
        prev != null && String(prev.code) === String(id) ? refreshed : prev
      );
    }
  }, [groups, accessDrawerOpen]);

  useEffect(() => {
    const styleId = "groups-list-component-styles";
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = styleId;
      styleElement.textContent = groupsListCSS;
      document.head.appendChild(styleElement);
    }
  }, []);

  const handleDeactivate = (status, group) => {
    let info;
    const title = status
      ? GROUPS_CONSTANTS.groupsList.ACTIVATE_CUSTOMER
      : GROUPS_CONSTANTS.groupsList.DEACTIVATE_CUSTOMER;
    const message = status
      ? GROUPS_CONSTANTS.groupsList.ACTIVATE_CONFIRM_MESSAGE
      : GROUPS_CONSTANTS.groupsList.DEACTIVATE_CONFIRM_MESSAGE;
    const action = handleEditGroupAction;
    info= {isDeleted : !status};
    setAlertInfo({ title, message, action, info, code: group.code });
    setAlertVisible(true);
  };

  return (
    <div className="admin-panel">
      {alertVisible && (
        <CustomAlert
          show={alertVisible}
          title={alertInfo.title}
          okText={
            alertInfo.info.isDeleted
              ? GROUPS_CONSTANTS.groupsList.BUTTON_DEACTIVATE
              : GROUPS_CONSTANTS.groupsList.BUTTON_ACTIVATE
          }
          onOkay={() => {
            alertInfo.action(alertInfo.info, alertInfo.code);
            setAlertVisible(false);
          }}
          onCancel={() => {
            setAlertVisible(false);
            setAlertInfo({});
          }}
          message={alertInfo.message}
        />
      )}

      <div className="search" style={{ display: "none" }}>
        <button className="search-button btn-user-bg form-control">
          <input
            className="btn-user-bg"
            type="text"
            placeholder="Search Group.."
            onChange={(search) => {
              handleSearchGroup(search.target.value);
            }}
          />
          <i className="bi bi-search"></i>
        </button>
      </div>
      <div className="col-12 row" style={groupsListStyles.rowContainer}>
        <div className="col-4">
          <button
            style={groupsListStyles.addNewButton}
            className="add-new add-new-bg"
            onClick={() => {
              navigate(GROUPS_CONSTANTS.navigation.CREATE_CUSTOMER_PATH);
            }}
          >
            <i
              className="bi bi-plus"
              style={groupsListStyles.addNewIcon}
            ></i>
            {GROUPS_CONSTANTS.groupsList.ADD_NEW_CUSTOMER}
          </button>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            {tableHeaders.list.map((headerTitle) => (
              <th key={headerTitle}>{headerTitle}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups?.map((group) => {
            const rowAccessDisabled = Boolean(group?.isDeleted);
            return (
              <tr
                key={group.code}
                className={
                  rowAccessDisabled
                    ? "groups-list-row-access groups-list-row-access--disabled"
                    : "groups-list-row-access"
                }
                aria-disabled={rowAccessDisabled}
                onClick={() => {
                  if (rowAccessDisabled) return;
                  setSelectedGroupForAccess(group);
                  setAccessDrawerOpen(true);
                }}
              >
                <td>
                  <span className="customer-name-link">{group.name}</span>
                </td>
                <td>{group.code}</td>
                <td
                  className="toggle"
                  style={groupsListStyles.toggleContainer}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={!group.isDeleted}
                      onChange={(e) => {
                        handleDeactivate(e.target.checked, group);
                      }}
                    />
                    <span className="slider"></span>
                    {""}
                  </label>
                </td>
                <td className="text-td-alignment" style={{ display: "none" }}>
                  <button
                    data-testid="edit-group-button"
                    className="edit-button"
                    aria-label={GROUPS_CONSTANTS.groupsList.ARIA_LABEL_EDIT_GROUP}
                    onClick={() => {
                      handelEditGroups(group);
                    }}
                  >
                    <i
                      className="bi bi-pencil-fill"
                      style={groupsListStyles.editIcon}
                    ></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Toast
        type={toast.type}
        title={toast.title}
        description={toast.description}
        show={toast.show}
        onClose={dismissToast}
        duration={GROUPS_CONSTANTS.toast.DURATION}
      />
      <SelectedCustomerAccessProvider value={selectedGroupForAccess}>
        <GroupAccessDrawer
          open={accessDrawerOpen}
          onClose={() => {
            setAccessDrawerOpen(false);
            setSelectedGroupForAccess(null);
          }}
          customerName={selectedGroupForAccess?.name ?? ""}
          customerCode={
            selectedGroupForAccess?.code ?? selectedGroupForAccess?.customerCode
          }
          modules={selectedGroupForAccess?.modules}
          onModulesUpdated={(modules) => {
            setSelectedGroupForAccess((prev) =>
              prev != null ? { ...prev, modules } : prev
            );
          }}
          onDataConnectionsApply={handleDataConnectionsApply}
          dataConnectionsApplying={dataConnectionsApplying}
          groups={groups}
          getAllGroups={getAllGroups}
        />
      </SelectedCustomerAccessProvider>
    </div>
  );
};

export default GroupsList;

GroupsList.propTypes = {
  groups: PropTypes.array,
  handelEditGroups: PropTypes.func.isRequired,
  handleEditGroupAction: PropTypes.func.isRequired,
  handleSearchGroup: PropTypes.func.isRequired,
  getAllGroups: PropTypes.func.isRequired,
};
